import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { ArrowLeft, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { GetSingleWishlistCollactionThunk } from "../../redux/states/cartCollection/thunk";

const ViewAllWishlistData = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();

  const { singlewishlist, loading } = useSelector((state) => state.cart);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    dispatch(GetSingleWishlistCollactionThunk(uuid));
  }, [dispatch, uuid]);

  // LOADING
  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-lg font-semibold">Loading wishlist details...</p>
        </div>
      </NonAuthLayout>
    );
  }

  // NOT FOUND
  if (!singlewishlist) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <XCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Wishlist Item Not Found</h2>

          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-2 rounded-xl"
          >
            Back
          </button>
        </div>
      </NonAuthLayout>
    );
  }

  // DATA
  const item = singlewishlist;
  const user = item?.wish_user;
  const product = item?.product_data;

  const getStatusBadge = (status) => {
    switch (status) {
      case "seen":
        return { color: "bg-green-100 text-green-800", icon: CheckCircle };
      case "unseen":
        return { color: "bg-red-100 text-red-800", icon: AlertCircle };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: AlertCircle };
    }
  };

  const statusBadge = getStatusBadge(item.view_status);
  const StatusIcon = statusBadge.icon;

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-3">
        {/* Header */}
        <div className="flex items-center justify-between bg-white p-3 rounded-xl shadow border">
          <button
            onClick={() => navigate(-1)}
            className="flex gap-2 items-center"
          >
            <ArrowLeft /> Back
          </button>

          <h3 className="font-bold text-gray-800">Wishlist Item Details</h3>

          <span
            className={`px-3 py-1 rounded-full flex items-center gap-1 ${statusBadge.color}`}
          >
            <StatusIcon className="w-4 h-4" />
            {item.view_status}
          </span>
        </div>

        {/* CONTENT CARD */}
        <div className="bg-white rounded-xl shadow border p-4 mt-3 space-y-6">
          {/* Wishlist Info */}
          <Section title="Wishlist Information">
            <InfoRow label="UUID" value={item.uuid} />
            <InfoRow
              label="Created At"
              value={new Date(item.created_at).toLocaleString("en-IN")}
            />
          </Section>

          {/* User Info */}
          <Section title="User Information">
            <InfoRow label="Name" value={user?.name} />
            <InfoRow label="Email" value={user?.email} />
            <InfoRow label="Mobile" value={user?.mobile} />
            <InfoRow label="City" value={user?.city} />
            <InfoRow label="State" value={user?.state} />
            <InfoRow label="Country" value={user?.country} />
          </Section>

          {/* Product Info */}
          <Section title="Product Information">
            <InfoRow label="Name" value={product?.name || "Product Removed"} />
            <InfoRow label="SKU" value={product?.sku} />
            <InfoRow label="Brand" value={product?.brand_name} />
            <InfoRow label="MRP" value={product ? `₹${product?.mrp}` : "—"} />
            <InfoRow
              label="Wholesale Price"
              value={product ? `₹${product?.wholesale_price}` : "—"}
            />
            <InfoRow label="Coupon" value={product?.coupon_code} />
          </Section>

          {/* IMAGE */}
          <div>
            <h3 className="font-semibold mb-2">Product Image</h3>

            {product?.image ? (
              <img
                src={`${API_URL}/${product?.image}`}
                className="h-60 rounded-xl border"
                alt="product"
              />
            ) : (
              <p className="text-gray-500 text-sm">No Image Available</p>
            )}
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

// ---------- Helper Components ----------
const Section = ({ title, children }) => (
  <div>
    <h3 className="text-md font-semibold text-gray-800 mb-2">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between bg-gray-50 px-4 py-2 rounded-lg border">
    <span className="font-medium text-gray-700">{label}</span>
    <span className="text-gray-900">{value || "—"}</span>
  </div>
);

export default ViewAllWishlistData;
